import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import Button from './Button';
import MegaMenu from './MegaMenu';
const MobileNavDrawer = lazy(() => import('./MobileNavDrawer'));

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  {
    label: 'The Atelier',
    href: '/atelier',
    children: [
      { label: 'Η Φιλοσοφία', href: '/atelier#philosophy' },
      { label: 'Ο Θοδωρής', href: '/atelier#theodore' },
      { label: 'Η Ομάδα', href: '/atelier/team' },
      { label: 'Κοινωνική Ευθύνη', href: '/atelier/impact' },
    ],
  },
  { label: 'Services', href: '/services', megaMenu: true },
  { label: 'Face Metrics Pro', href: '/services/face-metrics-pro' },
  { label: 'Hair Transformations', href: '/before-after' },
  { label: 'Thodoris Ikonomakis', href: '/thodoris-ikonomakis-hair-expert' },
  { label: 'Reviews', href: '/reviews' },
  {
    label: 'Journal',
    href: '/journal',
    children: [
      { label: 'Hair Advice', href: '/journal' },
      { label: 'Face Shape & Haircuts', href: '/ti-kourema-tairiazei-sto-prosopo-mou' },
      { label: 'Color Guides', href: '/ti-einai-to-balayage' },
      { label: 'Hair Trends', href: '/hair-transformation-guide' },
      { label: 'Hair Science', href: '/ti-einai-to-hair-glossing' },
      { label: 'Premium Hair Experience', href: '/ai-hair-analysis-greece' },
    ],
  },
];

const BOOKING_URL = 'https://www.fresha.com/a/ikonomakis-hair-atelier-halandri-irakleitoy-80-bxynmijf?pId=770996';

export default function Nav() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownTimer = useRef(null);
  const megaTimer = useRef(null);

  const openDropdownNow = (label) => {
    if (dropdownTimer.current) { clearTimeout(dropdownTimer.current); dropdownTimer.current = null; }
    setOpenDropdown(label);
  };

  const scheduleCloseDropdown = () => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    dropdownTimer.current = setTimeout(() => { setOpenDropdown(null); dropdownTimer.current = null; }, 200);
  };

  const openMegaNow = () => {
    if (megaTimer.current) { clearTimeout(megaTimer.current); megaTimer.current = null; }
    setMegaOpen(true);
  };

  const scheduleCloseMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    megaTimer.current = setTimeout(() => { setMegaOpen(false); megaTimer.current = null; }, 200);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`sticky top-0 z-40 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? 'bg-cream-bg/95 border-b border-brown/10 shadow-card-soft backdrop-blur-nav'
          : 'bg-cream-bg border-b border-brown/[0.08]'
      }`}>
        <div className="max-w-container mx-auto px-[6vw] py-5 flex justify-between items-center gap-5">
          <a href="/" className="flex items-baseline gap-1 font-serif text-[26px] text-warm-black tracking-tight shrink-0">
            <span className="font-semibold">Ikonomakis Hair Atelier</span>
          </a>

          <ul className="hidden xl:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <li
                key={link.label}
                className="relative"
                onMouseEnter={() => {
                  if (link.megaMenu) openMegaNow();
                  else if (link.children) openDropdownNow(link.label);
                }}
                onMouseLeave={() => {
                  if (link.megaMenu) scheduleCloseMega();
                  else if (link.children) scheduleCloseDropdown();
                }}
              >
                <a
                  href={link.href}
                  className="flex items-center gap-1 text-body-s font-medium text-warm-black hover:text-brown transition-colors py-2 whitespace-nowrap"
                >
                  {link.label}
                  {(link.children || link.megaMenu) && <span className="text-tiny">▾</span>}
                </a>

                {link.children && !link.megaMenu && openDropdown === link.label && (
                  <ul
                    className="absolute top-full left-0 mt-1 bg-ivory rounded-card-sm shadow-card py-3 min-w-[220px] animate-fade-in z-50"
                    onMouseEnter={() => openDropdownNow(link.label)}
                    onMouseLeave={scheduleCloseDropdown}
                  >
                    {link.children.map((child) => (
                      <li key={child.href}>
                        <a
                          href={child.href}
                          className="block px-5 py-2.5 text-body-s text-warm-black hover:bg-cream-soft hover:text-brown transition-colors"
                        >
                          {child.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="primary"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex !px-6 !py-3 !text-tiny"
            >
              Book Now
            </Button>

            <button
              className="xl:hidden flex flex-col gap-1.5 p-2"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <span className="block w-6 h-0.5 bg-warm-black" />
              <span className="block w-6 h-0.5 bg-warm-black" />
              <span className="block w-6 h-0.5 bg-warm-black" />
            </button>
          </div>
        </div>

        {megaOpen && (
          <div onMouseEnter={openMegaNow} onMouseLeave={scheduleCloseMega}>
            <MegaMenu onClose={() => setMegaOpen(false)} />
          </div>
        )}
      </nav>

      {mobileOpen && (
        <Suspense fallback={null}>
          <MobileNavDrawer
            isOpen={mobileOpen}
            onClose={() => setMobileOpen(false)}
            links={NAV_LINKS}
            bookingUrl={BOOKING_URL}
          />
        </Suspense>
      )}
    </>
  );
}